import apiClient from '../services/apiClient';
import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import AuthService from "../../modules/Admin/services/AuthService";
import NotificationService from "../../modules/Admin/services/NotificationService";
import ToastService from "../../modules/Admin/services/ToastService";
import { isValidGuid } from "../utils/guidValidation";
import { APP_LOGO_URL } from "../constants/branding";
import {
  FaBars,
  FaSearch,
  FaSignOutAlt,
  FaUser,
  FaUserAlt,
  FaSlidersH,
} from "react-icons/fa";
import { toggleSidebarOpen } from "../utils/sidebarMobile";
import SidebarBrand from "../components/SidebarBrand";
import { isAdminOrManagerRole, isSuperAdminRole } from "../../routes/roles";
import { isSessionTimeoutPending } from "../services/sessionTimeoutStore";

function Navbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(AuthService.isAuthenticated());
  const [searchTerm, setSearchTerm] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useRef(null);
  const toastService = useMemo(() => new ToastService(toast), []);
  const [userId, setUserId] = useState(AuthService.getLoggedInUserId());
  const [lastLoginTime, setLastLoginTime] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [assignedNotifications, setAssignedNotifications] = useState([]);
  const userRole = AuthService.getLoggedInRoleId();
  const [userImage, setUserImage] = useState("");

  useEffect(() => {
    const jwtUserId = AuthService.getLoggedInUserId();
    if (jwtUserId) {
      setUserId(jwtUserId);
    } else {
      console.error("User is not logged in.");
      navigate("/login");
    }
  }, []);

  const refreshNotifications = useCallback(async ({ showSpinner = false } = {}) => {
    if (!userId || AuthService.isChannelPartner()) {
      setLoading(false);
      return;
    }
    const token = AuthService.getToken();
    if (!token || AuthService.isTokenExpired(token) || isSessionTimeoutPending()) {
      setLoading(false);
      return;
    }
    if (showSpinner) setLoading(true);
    try {
      const response = await NotificationService.getAppNotifications(userId);
      setAssignedNotifications(response.leadAssignedNotifications ?? []);
      setTotalCount(response.totalCount ?? 0);
    } catch (error) {
      const expired =
        error?.code === "ERR_CANCELED"
        || /session expired/i.test(error?.message || "");
      if (!expired) {
        console.error("Error fetching notifications:", error);
      }
      setAssignedNotifications([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshNotifications({ showSpinner: true });
  }, [refreshNotifications]);

  useEffect(() => {
    if (!userId || AuthService.isChannelPartner()) return undefined;

    const poll = window.setInterval(() => {
      refreshNotifications();
    }, 60000);

    const onFocus = () => refreshNotifications();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [userId, refreshNotifications]);

  useEffect(() => {
    const toggle = document.getElementById("notifDropdown");
    if (!toggle) return undefined;
    const onShow = () => refreshNotifications();
    toggle.addEventListener("show.bs.dropdown", onShow);
    return () => toggle.removeEventListener("show.bs.dropdown", onShow);
  }, [refreshNotifications]);

  useEffect(() => {
    if (!userId) return;

    async function getUserImageById() {
      try {
        const response = await AuthService.getUserImageById(userId);
        setUserImage(response);
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error("Error fetching user image:", error);
        }
      }
    }

    getUserImageById();
  }, [userId]);

  useEffect(() => {
    const fetchLastLoginTime = async () => {
      try {
        const lastLogin = await AuthService.getLastLoginTime(userId);
        if (lastLogin) {
          const formattedTime = new Date(lastLogin).toLocaleString();
          setLastLoginTime(formattedTime);
        }
      } catch (error) {
        console.error("Error fetching last login time:", error);
      }
    };

    if (userId) {
      fetchLastLoginTime();
    }
  }, [userId]);

  const handleLogout = () => {
    if (toast.current) {
      toast.current.show({
        severity: "success",
        summary: "Logged Out",
        detail: "User Logged Out successfully!!",
        className: "custom-toast-delete",
      });
    }
    setIsLoggedIn(false);
    setTimeout(() => {
      AuthService.performLogout(navigate);
    }, 700);
  };

  const handleAccountClick = () => {
    const userId = AuthService.getLoggedInUserId();
    navigate(`/edituser/${userId}`);
  };

  function handleNotificationClick() {
    setShowNotifications(!showNotifications);
  }

  function closeNotificationMenu() {
    setShowNotifications(false);
    const toggle = document.getElementById("notifDropdown");
    if (!toggle) return;
    const Dropdown = window.bootstrap?.Dropdown;
    if (Dropdown) {
      Dropdown.getOrCreateInstance(toggle).hide();
      return;
    }
    toggle.setAttribute("aria-expanded", "false");
    toggle.closest(".dropdown")?.querySelector(".dropdown-menu")?.classList.remove("show");
  }

  async function handleAssignmentAlertClick(notification) {
    const leadAssignedId = notification?.leadAssignedId;
    const notificationId = notification?.notificationId;
    if (!isValidGuid(leadAssignedId)) {
      toastService.showError("Unavailable", "This lead is no longer available.");
      return;
    }
    closeNotificationMenu();
    if (isValidGuid(notificationId)) {
      try {
        await NotificationService.clearLeadAssignedNotificationById(notificationId);
      } catch (error) {
        if (error?.status === 404 || error?.response?.status === 404) {
          toastService.showError("Unavailable", "This lead is no longer available.");
          if (!isValidGuid(leadAssignedId)) return;
        } else {
          toastService.showWarning(
            "Notice",
            "Could not dismiss the alert. Opening the lead anyway."
          );
        }
      }
    }
    navigate(`/editleadassigned/${leadAssignedId}`);
    refreshNotifications({ showSpinner: true });
  }

  const toggleSidebar = () => {
    toggleSidebarOpen();
  };

  const handleSearch = () => {
    const term = searchTerm.trim();
    if (term !== "") {
      navigate(`/search?query=${encodeURIComponent(term)}`);
    }
    setSearchTerm("");
  };

  const notificationBadgeCount = totalCount;

  function formatTimeElapsed(timestamp) {
    const timeElapsed = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (timeElapsed < 60) {
      return `${timeElapsed} seconds ago`;
    } else if (timeElapsed < 3600) {
      return `${Math.floor(timeElapsed / 60)} minutes ago`;
    } else if (timeElapsed < 86400) {
      return `${Math.floor(timeElapsed / 3600)} hours ago`;
    } else {
      return `${Math.floor(timeElapsed / 86400)} days ago`;
    }
  }

  const roleName = isSuperAdminRole(userRole)
    ? "Super Admin"
    : isAdminOrManagerRole(userRole)
      ? "Admin"
      : "Employee";

  const hasValidUserImage =
    typeof userImage === "string" &&
    userImage.length > 0 &&
    userImage !== "undefined" &&
    userImage !== "null";

  const jwtUser = AuthService.getUser?.() ?? {};
  const displayName =
    jwtUser.UserName ??
    jwtUser.userName ??
    AuthService.getRememberedCredentials?.()?.email ??
    "Account";

  const renderProfileAvatar = (size = "sm") => {
    const cls = size === "lg" ? "nf-profile-menu-avatar" : "nb-profile-avatar";
    if (hasValidUserImage) {
      return (
        <div className={cls}>
          <img src={`data:image/jpeg;base64,${userImage}`} alt="" />
        </div>
      );
    }
    return (
      <div className={`${cls} is-brand-fallback`}>
        <img src={APP_LOGO_URL} alt="NextFollowUp" />
      </div>
    );
  };

  return (
    <nav className="nf-topbar-nav" id="navbarBlur">
      <Toast ref={toast} />

      <div className="nf-topbar-left">
        <button
          type="button"
          className="nb-iconbtn nb-togglebtn"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
        >
          <FaBars />
        </button>
        <SidebarBrand to="/dashboard" variant="topbar" />
      </div>

      <div className="nf-topbar-right">
        <div className="nb-toolbar">
          <div style={{ position: "relative", flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
            <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#65676b", fontSize: "13px", pointerEvents: "none" }} />
            <input
              className="nb-searchinp"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => { if (e.key === "Enter") { handleSearch(); setSearchTerm(""); } }}
              placeholder="Search leads…"
              aria-label="Search leads"
              style={{
                width: "100%",
                padding: "7px 12px 7px 32px",
                border: "none",
                borderRadius: "20px",
                fontSize: "13px",
                color: "#050505",
                background: "transparent",
              }}
            />
          </div>

          <div className="nav-item dropdown" style={{ position: "relative", flexShrink: 0 }}>
            <button
              type="button"
              className={`nb-bellbtn ${notificationBadgeCount > 0 ? "has-alerts" : "no-alerts"}`}
              id="notifDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label={`Notifications${notificationBadgeCount > 0 ? `, ${notificationBadgeCount} unread` : ""}`}
            >
              <i
                className="fas fa-bell"
                style={{
                  fontSize: "15px",
                  color: notificationBadgeCount > 0 ? "#1877f2" : "#65676b",
                }}
              />
              {notificationBadgeCount > 0 && (
                <span className="nb-badge-count">
                  {notificationBadgeCount > 99 ? "99+" : notificationBadgeCount}
                </span>
              )}
            </button>
          <div
            className="dropdown-menu dropdown-menu-end"
            aria-labelledby="notifDropdown"
            style={{ minWidth: "320px", border: "none", borderRadius: "16px", boxShadow: "0 10px 40px rgba(52,71,103,0.15)", padding: "0", overflow: "hidden", marginTop: "8px" }}
          >
            {/* Header */}
            <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid #f0f2f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontWeight: "700", color: "#344767", fontSize: "14px" }}>Notifications</p>
                <p style={{ margin: 0, fontSize: "11px", color: "#8392ab" }}>Lead assignment alerts</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {(notificationBadgeCount) > 0 && (
                  <span style={{ background: "#f0f2ff", color: "#5e72e4", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", fontWeight: "700" }}>
                    {notificationBadgeCount} new
                  </span>
                )}
                <button
                  className="nb-iconbtn"
                  onClick={() => refreshNotifications({ showSpinner: true })}
                  title="Refresh"
                  style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#f4f6fb", color: "#8392ab", fontSize: "12px" }}
                >
                  <i className="fas fa-sync-alt" />
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ maxHeight: "360px", overflowY: "auto", padding: "8px" }}>
              {loading ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#8392ab", fontSize: "13px" }}>
                  <i className="fas fa-circle-notch fa-spin" style={{ marginRight: "8px" }} />Loading…
                </div>
              ) : (
                <>
                  {assignedNotifications && assignedNotifications.length > 0 ? (
                    assignedNotifications.map((n) => (
                      <button
                        type="button"
                        key={n.notificationId || n.leadAssignedId}
                        className="nb-notif-item"
                        onClick={() => handleAssignmentAlertClick(n)}
                      >
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                          <div style={{ width: "32px", height: "32px", borderRadius: "10px", background: "#f0f2ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#5e72e4", fontSize: "13px", flexShrink: 0 }}>
                            <i className="fas fa-user-check" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: "10px", color: "#5e72e4", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Lead assignment
                            </p>
                            <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#344767", fontWeight: "600", lineHeight: 1.4 }}>
                              {n.leadDisplayName || n.message}
                            </p>
                            <p style={{ margin: "3px 0 0", fontSize: "11px", color: "#8392ab" }}>
                              <i className="fas fa-clock" style={{ marginRight: "4px" }} />
                              {n.date ? formatTimeElapsed(n.date) : "Today"}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : notificationBadgeCount === 0 ? (
                    <div style={{ padding: "28px 16px", textAlign: "center" }}>
                      <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#f4f6fb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: "18px", color: "#c0c8d8" }}>
                        <i className="fas fa-bell-slash" />
                      </div>
                      <p style={{ color: "#8898aa", fontSize: "12px", fontWeight: "600", margin: 0 }}>No new notifications</p>
                      <p style={{ color: "#c0c8d8", fontSize: "11px", margin: "6px 0 0" }}>
                        New lead assignments appear here.
                      </p>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
          </div>
        </div>

        {/* Profile */}
        <div className="nav-item dropdown nf-profile-dropdown">
          <button
            type="button"
            className="nb-iconbtn nb-profile-btn"
            id="profileDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title={roleName}
            aria-label="Account menu"
          >
            {renderProfileAvatar("sm")}
          </button>

          <div
            className="dropdown-menu dropdown-menu-end nf-profile-menu"
            aria-labelledby="profileDropdown"
          >
            <div className="nf-profile-menu-head">
              {renderProfileAvatar("lg")}
              <div className="nf-profile-menu-copy">
                <p className="nf-profile-menu-name">{displayName}</p>
                <span className="nf-profile-menu-role">{roleName}</span>
                {lastLoginTime ? (
                  <span className="nf-profile-menu-meta">Last login {lastLoginTime}</span>
                ) : null}
              </div>
            </div>

            <div className="nf-profile-menu-body">
              <button type="button" className="nf-profile-menu-item" onClick={handleAccountClick}>
                <span className="nf-profile-menu-item-icon" aria-hidden="true">
                  <FaUser />
                </span>
                Your profile
              </button>

              {isLoggedIn ? (
                <button
                  type="button"
                  className="nf-profile-menu-item"
                  onClick={() => navigate("/preferences")}
                >
                  <span className="nf-profile-menu-item-icon" aria-hidden="true">
                    <FaSlidersH />
                  </span>
                  My preferences
                </button>
              ) : null}

              {isLoggedIn ? (
                <button type="button" className="nf-profile-menu-item is-danger" onClick={handleLogout}>
                  <span className="nf-profile-menu-item-icon is-danger" aria-hidden="true">
                    <FaSignOutAlt />
                  </span>
                  Log out
                </button>
              ) : (
                <button type="button" className="nf-profile-menu-item" onClick={() => navigate("/login")}>
                  <span className="nf-profile-menu-item-icon" aria-hidden="true">
                    <FaUserAlt />
                  </span>
                  Sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
